import { Box, CircularProgress, FormHelperText, Typography, useTheme } from "@mui/material";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import SignatureBox from "../../../components/common/SignatureBox";
import { useField } from "formik";
import { useParams } from "react-router-dom";
import { at } from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";
import { 
  useGetSignaturePreferencesQuery, 
  useUpdateSignaturePreferencesMutation,
} from "../../../store/slices/apiSlice";

const SignatureField = (props) => {
  const {
    setFieldValue,
    parentKey,
    disabled,
    hasSignAccess = true,
    sectionKey,
    fieldKey,
    subSectionKey,
    values,
    handleSaveSignature,
    handleDownloadSignature,
    role,
  } = props;
  let { siteInitStep } = useParams();
  const theme = useTheme();
  const signatureUploading = useSelector((state) => state.application.dynamicForm.signatureUploading);
  const userData = useSelector((state) => state.auth.userData);
  
  // Fetch signature preferences from dedicated API
  // This query provides 'userSignaturePreferences' tag and auto-refetches when invalidated
  const { data: signaturePreferencesData } = useGetSignaturePreferencesQuery();
  
  // RTK Query mutation for updating signature preferences
  // This mutation invalidates 'userSignaturePreferences' tag, causing the query to refetch
  const [updateSignaturePreferences] = useUpdateSignaturePreferencesMutation();
  
  const [sign, setSign] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [signatureDialog, setSignatureDialog] = useState(false);
  const [signTime, setSignTime] = useState(moment().format("DD/MM/YYYY HH:mm:ss"));
  const [field, meta, helpers] = useField(props);
  const [touched, error] = at(meta, "touched", "error");
  // Ref to track the last fetched signature key to prevent duplicate API calls
  const lastFetchedSignatureRef = useRef(null);
  // Don't show error if signature is visually present (sign state) or field has value
  const isError = touched && error && !sign && !field.value;
  const isDisabled = disabled || signatureUploading;
  
  // Get user signature preferences from the dedicated query
  // Falls back to userData.userMetaInfo if query hasn't loaded yet
  const userSignaturePreferences = useMemo(() => {
    if (signaturePreferencesData) {
      return {
        signaturePreference: signaturePreferencesData.signaturePreference || 'draw',
        signatureFont: signaturePreferencesData.signatureFont || 'Dancing Script',
      };
    }
    // Fallback to userData while query is loading
    if (userData?.userMetaInfo) {
      return {
        signaturePreference: userData.userMetaInfo.signaturePreference || 'draw',
        signatureFont: userData.userMetaInfo.signatureFont || 'Dancing Script',
      };
    }
    return null;
  }, [signaturePreferencesData, userData?.userMetaInfo]);

  const openDialog = () => {
    if (!isDisabled) {
      setSignatureDialog(true);
    }
  };

  const handleClose = () => {
    setSignatureDialog(false);
  };
  
  // Helper function to check if signature value is valid for downloading
  const isValidSignatureValue = (value) => {
    if (value === null || value === undefined) return false;
    if (value === '') return false;
    if (value === 'testSign') return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    // Check for array with empty/null values
    if (Array.isArray(value)) {
      return value.length > 0 && value.some(v => v !== null && v !== undefined && v !== '');
    }
    return true;
  };

  useEffect(() => {
    const signatureValue = field.value;
    
    // Only fetch if signature value is valid AND hasn't been fetched yet
    if (isValidSignatureValue(signatureValue) && lastFetchedSignatureRef.current !== signatureValue) {
      // Mark this signature as being fetched to prevent duplicate calls
      lastFetchedSignatureRef.current = signatureValue;
      setSignatureLoading(true);
      setSignatureError(false);
      
      // Use e-consent download API for all signatures (single source of truth)
      handleDownloadSignature(signatureValue, (signData) => {
        if (signData) {
          setSign(signData);
        } else {
          setSignatureError(true);
        }
        setSignatureLoading(false);
      });
    }
    // Note: handleDownloadSignature is intentionally excluded from deps to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value])

  useEffect(() => {
    // Clear any validation error if signature exists (either visually or as field value)
    if (error && (sign || field.value)) {
      helpers.setError(undefined);
    }
  }, [sign, field.value, error])

  // Handler to update user signature preferences via API
  // After successful update, the query will auto-refetch due to tag invalidation
  const handleUpdatePreferences = useCallback(async (preferences) => {
    try {
      await updateSignaturePreferences({
        signaturePreference: preferences.signaturePreference,
        signatureFont: preferences.signatureFont,
      }).unwrap();
      // No manual refetch needed - RTK Query will auto-refetch due to invalidatesTags
    } catch (error) {
      console.error('Failed to update signature preferences:', error);
    }
  }, [updateSignaturePreferences]);

  const saveSignature = async (data, signatureMetadata = {}) => {
    if (!data) {
      setSignatureDialog(false);
      helpers.setError("This field is required");
    } else {
      const currentSignTime = moment().format("DD/MM/YYYY HH:mm:ss");
      setSignTime(currentSignTime);
      setSign(data);
      setSignatureDialog(false);
      
      // Determine the signature date field key
      const signDateKey = field.currentDateKey || `${fieldKey}_sign_date`;
      const signModeKey = `${fieldKey}_sign_mode`;
      const signerNameKey = `${fieldKey}_signer_name`;
      
      // Get signer name from user data
      const signerName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : '';
      const signMode = signatureMetadata.mode || 'draw';
      
      // All signature modes (draw, type, upload) use the same e-consent upload flow
      // This ensures single source of truth for all signatures
      let keys = {
        stepKey: siteInitStep,  
        sectionKey: sectionKey,
        subSectionKey: subSectionKey,
        categoryKey: parentKey ? parentKey : "",
        fieldKey: fieldKey,
        file: data,
        signatureMode: signMode,
        signatureFont: signatureMetadata.font || null,
      };
      
      try {
        // signatureUploading state is automatically managed by API matchers in applicationSlice
        await handleSaveSignature(keys, field.name, setFieldValue);
        
        // Set the signature metadata after saving
        if (parentKey) {
          setFieldValue(`${parentKey}.${signDateKey}`, currentSignTime);
          setFieldValue(`${parentKey}.${signModeKey}`, signMode);
          setFieldValue(`${parentKey}.${signerNameKey}`, signerName);
        } else {
          setFieldValue(signDateKey, currentSignTime);
          setFieldValue(signModeKey, signMode);
          setFieldValue(signerNameKey, signerName);
        }
      } catch (error) {
        console.error('Failed to save signature:', error);
        helpers.setError("Failed to save signature");
        // Clear the visual signature since save failed
        setSign(null);
      }
    }
  };
  
  function _renderHelperText() {
    // Only show error if there's no signature (both visual and field value)
    if (isError && !sign && !field.value) {
      return <FormHelperText sx={{ color: "#d32f2f" }}>{error}</FormHelperText>;
    }
    return <FormHelperText sx={{ color: "#d32f2f" }}>{""}</FormHelperText>;
  }
  
  const renderDate = () => {
    return field.currentDateKey
      ? parentKey
        ? values[parentKey]?.[field.currentDateKey]
        : values[field.currentDateKey]
      : (parentKey ? values[parentKey]?.[`${fieldKey}_sign_date`] : values[`${fieldKey}_sign_date`]) || signTime ;
  };
  
  // Get signature mode from form values
  const getSignMode = () => {
    const mode = parentKey 
      ? values[parentKey]?.[`${fieldKey}_sign_mode`] 
      : values[`${fieldKey}_sign_mode`];
    return mode || null;
  };
  
  // Get signer name from form values
  const getSignerName = () => {
    const name = parentKey 
      ? values[parentKey]?.[`${fieldKey}_signer_name`] 
      : values[`${fieldKey}_signer_name`];
    return name || null;
  };
  
  // Format signature mode for display
  const formatSignMode = (mode) => {
    if (!mode) return null;
    const modeLabels = {
      'draw': 'Drawn',
      'type': 'Typed',
      'upload': 'Uploaded'
    };
    return modeLabels[mode] || mode;
  };

  return (
    <Box
      sx={{
        height: props.justifycontent ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        rowGap: 1,
        justifyContent: props.justifycontent ? 'start' : 'space-between',
      }}
    >
      {props.label && (
        <Typography sx={{ color: '#5b5b5b', fontWeight: 'bold' }} variant="subtitle1" gutterBottom>
          {props.label}
          {props.label && props.required && <span style={{ color: 'red', fontSize: 18 }}>*</span>}
        </Typography>
      )}
      {props.label2 && (
        <Typography sx={{ color: '#5b5b5b', fontWeight: 'medium' }} variant="subtitle1" gutterBottom>
          {props.label2}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        {role && <Typography variant="subtitle1">{role}</Typography>}
      </Box>
      <Box
        sx={{
          boxShadow: isError
            ? `0px 0px 8px 0px ${theme.palette.error.main}`
            : isDisabled
            ? 'none'
            : '1px 1px 4px 0 rgba(0, 0, 0, 0.2), 0 2px 6px 0 rgba(0, 0, 0, 0.10)',
          height: 80,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: signatureUploading ? 0.6 : isDisabled ? 0.7 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          backgroundColor: isDisabled ? theme.palette.grey[100] : 'transparent',
          border: isDisabled ? `1px dashed ${theme.palette.grey[400]}` : 'none',
          borderRadius: isDisabled ? '4px' : '0',
        }}
      >
        {signatureUploading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Uploading signature...</Typography>
          </Box>
        ) : signatureLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading signature...</Typography>
          </Box>
        ) : signatureError ? (
          <Typography
            onClick={openDialog}
            sx={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              color: theme.palette.error.main,
            }}
          >
            {isDisabled ? 'Failed to load signature' : 'Failed to load signature. Click to sign again'}
          </Typography>
        ) : sign ? (
          <img
            src={sign}
            width={250}
            height={70}
            disabled={isDisabled}
            alt={isDisabled ? (!hasSignAccess ? 'Signature (No Access)' : 'Signature (View Only)') : 'Click here to sign'}
            onClick={openDialog}
            style={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              filter: isDisabled ? 'grayscale(30%)' : 'none',
            }}
          />
        ) : (
          <Typography
            onClick={openDialog}
            sx={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              color: isDisabled ? theme.palette.grey[500] : 'inherit',
              fontStyle: isDisabled ? 'italic' : 'normal',
            }}
          >
            {isDisabled 
              ? !hasSignAccess 
                ? 'You do not have signing access for this field' 
                : 'Signature not available'
              : 'Click here to sign'}
          </Typography>
        )}
      </Box>
      {sign && (
          <Box sx={{ marginLeft: 'auto', textAlign: 'right' }}>
            <Typography variant="subtitle1">{renderDate()}</Typography>
            {getSignerName() && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Signed by: {getSignerName()}
              </Typography>
            )}
            {getSignMode() && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                ({formatSignMode(getSignMode())})
              </Typography>
            )}
          </Box>
        )}
      <SignatureBox
        open={signatureDialog}
        handleClose={handleClose}
        saveSign={saveSignature}
        userSignaturePreferences={userSignaturePreferences}
        onUpdatePreferences={handleUpdatePreferences}
      />
      <input type="hidden" {...field} />
      {_renderHelperText()}
    </Box>
  );
};

export default React.memo(SignatureField);
